import { ModalTabHandle } from "./modalTabHandle";
import type { ModalTabItem } from "./tabbed-modal-types";

export class ModalTabsCollection {
	private handles: ModalTabHandle[] = [];
	private availableHandles: ModalTabHandle[] = [];

	public constructor(private readonly onRegistered?: (handle: ModalTabHandle) => void) {}

	public add(handle: ModalTabHandle): ModalTabHandle {
		if(!handle.hash) {
			throw new Error("A tabbed-modal tab requires a non-empty code.");
		}

		if(this.handles.some(existing => existing.hash === handle.hash)) {
			throw new Error(`A tabbed-modal tab with code "${handle.code}" is already registered.`);
		}

		this.handles.push(handle);
		if(handle.isAvailable) {
			this.availableHandles = [...this.availableHandles, handle];
		}
		this.onRegistered?.(handle);

		return handle;
	}

	public get available(): ReadonlyArray<ModalTabHandle> {
		return this.availableHandles;
	}

	public get firstSelectable(): ModalTabHandle | undefined {
		return this.available.find(handle => handle.isSelectable);
	}

	public findSelectable(
		tab?: ModalTabHandle | ModalTabItem | string | null
	): ModalTabHandle | undefined {
		return this.available.find(handle => handle.isSelectable && handle.isSame(tab));
	}

	public findSelectableByCode(code?: string | null): ModalTabHandle | undefined {
		return this.findSelectable(code);
	}

	public resolveTab(codes: ReadonlyArray<string | null | undefined>): ModalTabHandle | undefined {
		for(const code of codes) {
			const tab = this.findSelectableByCode(code);
			if(tab) {
				return tab;
			}
		}

		return undefined;
	}

	public isSame(
		first?: ModalTabHandle | ModalTabItem | string | null,
		second?: ModalTabHandle | ModalTabItem | string | null
	): boolean {
		const firstHash = this.getHash(first);
		return firstHash !== "" && firstHash === this.getHash(second);
	}

	private getHash(tab?: ModalTabHandle | ModalTabItem | string | null): string {
		if(tab instanceof ModalTabHandle) {
			return tab.hash;
		}

		return ModalTabHandle.normalizeCode(typeof tab === "string" ? tab : tab?.code);
	}
}
